import { EditorState } from 'draft-js';
// graphql, interfaces
import { LoadAddUnit, OrgDashboardPayload, ServingUnitType, SuperDashboardPayload } from 'generated/graphql';
import {
  AddBottleCapacityFormType,
  AddBottlePriceFormType,
  AddBottleSizeFormType,
  AddExcipientFormType,
  AddExcipientLabelFormType,
  AddExcipientTypeFormType,
  AddNewFormulaFormType,
  AddNewIngredientFormType,
  AddNewStaffFormType,
  AddOrgStaffFormType,
  AddNewUserFormType,
  ProductExcipientFormType,
  AddProductFormulationTypeForm,
  AddProductSizeFormType,
  AddProductTypeFormType,
  AddSubProductTypeFormType,
  AddSupplierFormType,
  AllergensCardType,
  BlendItemType,
  ChangeFormType,
  ContactFormType,
  DosageFormulationFormType,
  EditStaffFormType,
  EditUserFormType,
  FactBoxFormulationFormType,
  FormulaInfoCardFormType,
  FormulaIngredientFormType,
  FormulationFormType,
  HeavyMetalsFormType,
  IngredientBasicInfoCardType,
  IngredientCostInfoCardType,
  IngredientCountriesCardType,
  IngredientDensityCardType,
  IngredientItemFormType,
  IngredientSupplierCardType,
  LoginUserInput,
  NutrientsCardType,
  NutrientsTypeObject,
  OrganizationFormType,
  ProductTypeSelect,
  SaveFormulaAsDraftFormType,
  SelectType,
  StaffRoleFormType,
  SupplierFormType,
  UpdatePasswordFormType,
  UpdateUserProfileType,
  UserFormType,
  AddSweetenerSystemFormType,
  AddSweetenerComponentFormType,
  AddSweetenerElementFormType,
  CarriersFormType,
  CarrierSelectType,
  SubComponentSelectType,
  SubComponentsFormType,
  AdditionalContactSelectType,
  AdditionalContactFormType,
  ProductIngredientFormType,
  FormulationSyrupTypeFormType,
  IngredientSyrupTypeFormType,
  AddNewSyrupBaseIngredientFormType,
  SyrupBaseIngredientBasicInfoCardType,
  AddServingSizeFormType,
  SandCoatingFormType,
  SandCoatingFormulationFormType,
  SandComponentFormType,
  CoatingFormulationComponentFormType,
  CapsuleTypeFormType,
  CapsulePriceFormType,
  SignupUserInput,
  StripeFormType,
  AddressFormType,
  VerifyOTPInput,
  BaseCoatingFormType,
  DvConstraintsFormType,
  FormulaAgeGroupSelectType,
} from 'interfaces';

export const selectTypeInitialValues: SelectType = {
  name: '',
  value: '',
  type: '',
};

export const loginUserInitialValues: LoginUserInput = {
  email: '',
  password: '',
  rememberMe: false,
};

export const VerifyOTPInitialValues: VerifyOTPInput = {
  otp: '',
};

export const stripeFormInitialValues: StripeFormType = {
  name: '',
  card: '',
  expiry: '',
  cvc: '',
};

export const signupUserInitialValues: SignupUserInput = {
  firstName: '',
  lastName: '',
  email: '',
  organizationName: '',
  termsAndConditions: false,
};

export const organizationInitialValue: OrganizationFormType = {
  organizationName: '',
  maxUser: '',
};

export const roleInitialValue: StaffRoleFormType = {
  role: '',
};

export const updatePasswordInitialValues: UpdatePasswordFormType = {
  password: '',
};

export const userInitialValues: UserFormType = {
  email: '',
  phone: '',
  lastName: '',
  firstName: '',
};

export const addressInitialValues: AddressFormType = {
  address1: '',
  address2: '',
  city: '',
  zip: '',
  state: selectTypeInitialValues,
  country: selectTypeInitialValues,
};

export const editUserInitialValues: EditUserFormType = {
  email: '',
  phone: '',
  lastName: '',
  firstName: '',
  organizationName: '',
  maxUser: '',
};

export const updateStaffInitialValues: EditStaffFormType = {
  ...userInitialValues,
  ...roleInitialValue,
};

export const addNewStaffInitialValue: AddNewStaffFormType = {
  ...roleInitialValue,
  ...userInitialValues,
  ...updatePasswordInitialValues,
};

export const addOrgStaffInitialValue: AddOrgStaffFormType = {
  ...addNewStaffInitialValue,
  priceId: '',
  trialEnd: '',
};

export const addNewUserInitialValues: AddNewUserFormType = {
  ...userInitialValues,
  ...organizationInitialValue,
  ...updatePasswordInitialValues,
  ...addressInitialValues,
};

export const carrierSelectTypeInitialValue: CarrierSelectType = {
  excipient: selectTypeInitialValues,
  percentage: '',
};

export const subComponentSelectTypeInitialValue: SubComponentSelectType = {
  name: '',
  percentage: '',
};

export const formulaAgeGroupInitialValue: FormulaAgeGroupSelectType = {
  ageGroupType: '',
  dvType: '',
  servingSize: '',
  servingUnit: '',
  servingContainer: '',
};

export const ingredientBasicInfoCardInitialValues: IngredientBasicInfoCardType = {
  name: '',
  latinName: EditorState.createEmpty(),
  tradeMarkName: '',
  scientificName: '',
  type: [],
  category: selectTypeInitialValues,
  subCategory: selectTypeInitialValues,
  productTypes: [],
  description: '',
  isOrganic: false,
};

export const SyrupBaseIngredientBasicInfoCardInitialValues: SyrupBaseIngredientBasicInfoCardType = {
  name: '',
  type: [],
  productFormulationSyrupType: selectTypeInitialValues,
};

export const ingredientCostInfoCardInitialValues: IngredientCostInfoCardType = {
  amount: `1`,
  showB3: false,
  nutrients: false,
  nutrientName: false,
  cost: '',
  potency: '',
  shelfLife: '',
  suggestedOverage: '',
  gummyOverage: '',
  proBiotic: '',
};

export const ingredientSupplierCardInitialValues: IngredientSupplierCardType = {
  primarySupplier: selectTypeInitialValues,
  secondarySupplier: selectTypeInitialValues,
  tertiarySupplier: selectTypeInitialValues,
};

export const ingredientCountriesCardInitialValues: IngredientCountriesCardType = {
  manufacturingCountry: selectTypeInitialValues,
  originCountry: selectTypeInitialValues,
};

export const ingredientDensityCardInitialValues: IngredientDensityCardType = {
  lossOnDrying: '',
  density: '',
  averageParticleSize: '',
};

export const allergensCardInitialValue: AllergensCardType = {
  allergens: [],
};

export const nutrientsCardInitialValues: NutrientsTypeObject = {
  organic: '',
  moisture: '',
  calories: '',
  protein: '',
  carbohydrates: '',
  dietaryFiber: '',
  totalSugars: '',
  addedSugar: '',
  totalFat: '',
  saturatedFat: '',
  monounsaturatedFat: '',
  polyunsaturatedFat: '',
  transFat: '',
  vitaminA: '',
  biotin: '',
  thiamin: '',
  riboflavin: '',
  niacin: '',
  vitaminB6: '',
  vitaminB12: '',
  vitaminC: '',
  vitaminD: '',
  vitaminE: '',
  magnesium: '',
  zinc: '',
  vitaminK2: '',
  choline: '',
  iodine: '',
  folate: '',
  cholesterol: '',
  calcium: '',
  iron: '',
  potassium: '',
  boron: '',
  sodium: '',
  chloride: '',
  chromium: '',
  copper: '',
  manganese: '',
  molybdenum: '',
  pantothenicAcid: '',
  phosphorus: '',
  selenium: '',
};

export const heavyMetalsInitialValues: HeavyMetalsFormType = {
  arsenic: '',
  cadmium: '',
  lead: '',
  mercury: '',
};

export const nutrientsInitialValues: NutrientsCardType = {
  ingredientInfo: nutrientsCardInitialValues,
};

export const carriersFormTypeInitialValues: CarriersFormType = {
  carriers: [],
};

export const subComponentFormTypeInitialValue: SubComponentsFormType = {
  subComponents: [],
};

export const addNewIngredientInitialValues: AddNewIngredientFormType = {
  ...ingredientBasicInfoCardInitialValues,
  ...ingredientCostInfoCardInitialValues,
  ...ingredientSupplierCardInitialValues,
  ...ingredientCountriesCardInitialValues,
  ...ingredientDensityCardInitialValues,
  ...allergensCardInitialValue,
  ...nutrientsInitialValues,
  ...heavyMetalsInitialValues,
  ...carriersFormTypeInitialValues,
  ...subComponentFormTypeInitialValue,
};
export const addNewSyrupBaseIngredientInitialValues: AddNewSyrupBaseIngredientFormType = {
  ...ingredientBasicInfoCardInitialValues,
  ...heavyMetalsInitialValues,
  ...nutrientsInitialValues,
  productFormulationSyrupType: selectTypeInitialValues,
};

export const changePasswordInitialValues: ChangeFormType = {
  conPassword: '',
  newPassword: '',
  oldPassword: '',
};

export const productFormulationTypeInitialValues: ProductTypeSelect = {
  name: '',
  value: '',
  type: '',
};

export const IngredientsItemFormInitialValues: IngredientItemFormType = {
  value: '',
  potency: 1,
  overage: 0,
  name: '',
  ingredientPotency: 1,
};

export const blendItemInitialValues: BlendItemType = {
  name: '',
  ingredients: [],
};

export const addNewFormulaInitialValue: AddNewFormulaFormType = {
  capOverride: false,
  name: '',
  formulaBlend: [],
  formulaIngredients: [],
  ingredients: [],
};

export const productTypeInitialValues: Pick<DosageFormulationFormType, 'productType'> = {
  productType: '',
};

export const formulationValues: FormulationFormType = {
  beadlets: '',
  colorsHex: [],
  bottleCap: '',
  bottleType: '',
  coMfgCost: '',
  coMfgCostType: '',
  description: '',

  tabletType: '',
  capsuleType: '',
  tabletCoating: '',
  excipientLabel: '',
  innerCapsuleType: '',
  outerCapsuleType: '',
  desiredServingSize: '',
  coMfgCostPercentage: '',
  servingPerContainer: '',
  servingUnit: ServingUnitType.Capsule,
  excipientType: selectTypeInitialValues,
  miscellaneousCost: '',

  ageGroups: [formulaAgeGroupInitialValue],

  coatingColor: '',
  coatingColor2: '',
  coatingColor3: '',

  miniTabFormulation: '',

  flavorType: '',
  flavorSystem: selectTypeInitialValues,

  sweetenerType: '',
  sweetenerSystem: selectTypeInitialValues,

  factBoxType: '',
  proteinBase: '',
  veganType: '',

  productSize: selectTypeInitialValues,
  sandCoating: '',
};

export const dosageFormulaFormInitialValues: Omit<DosageFormulationFormType, 'productType'> = {
  productSubType: '',
  ...formulationValues,
  ...productTypeInitialValues,
  productFormulationType: productFormulationTypeInitialValues,
};

export const dosageFormulationFormInitialValues = {
  ...dosageFormulaFormInitialValues,
  ...productTypeInitialValues,
};

export const UpdateUserProfileInitialValues: UpdateUserProfileType = {
  email: '',
  phone: '',
  lastName: '',
  firstName: '',
  orgName: '',
};

export const factBoxFormulaFormInitialValues: FactBoxFormulationFormType = {
  capOverride: false,
  factBoxType: '',
  formulaIngredients: [],
  ingredients: [],
};

export const formulaInfoCardFormInitialValue: FormulaInfoCardFormType = {
  name: '',
};

export const formulaIngredientFormInitialValues: FormulaIngredientFormType = {
  capOverride: false,
  ingredients: [],
  formulaIngredients: [],
  primary: selectTypeInitialValues,
  secondary: selectTypeInitialValues,
};

export const superInitialValues: SuperDashboardPayload = { ingredients: 0 };
export const orgInitialValues: OrgDashboardPayload = { ingredients: 0 };

export const saveFormulaAsDraftFormInitialValue: SaveFormulaAsDraftFormType = {
  name: '',
};

export const contactInitialValues: ContactFormType = {
  // id: '',
  city: '',
  name: '',
  email: '',
  phoneNo: '',
  zipCode: '',
  address2: '',
  address1: '',
  designation: '',
  state: selectTypeInitialValues,
  country: selectTypeInitialValues,
};

export const additionalContactSelectTypeInitialValue: AdditionalContactSelectType = {
  name: '',
  email: '',
};

export const additionalContactsFormTypeInitialValue: AdditionalContactFormType = {
  additionalContacts: [],
};
export const supplierInitialValues: SupplierFormType = {
  companyName: '',
  description: '',
};

export const addSupplierInitialValues: AddSupplierFormType = {
  ...additionalContactsFormTypeInitialValue,
  ...contactInitialValues,
  ...supplierInitialValues,
};

export const productTypeInitialValue: AddProductTypeFormType = {
  name: '',
  propagateToOrganizations: false,
};

export const productSubTypeInitialValue: AddSubProductTypeFormType = {
  name: '',
  productType: selectTypeInitialValues,
};

export const productSizeInitialValue: AddProductSizeFormType = {
  name: '',
  productType: selectTypeInitialValues,
  subProductType: selectTypeInitialValues,
  emptyWtMg: '',
  min: '',
  max: '',
  gummyWeight: '',
};

export const productFormulationTypeInitialValue: AddProductFormulationTypeForm = {
  name: '',
  isActive: true,
  lohInside: '',
  conversionCost: '0',
  isInnerCapTab: false,
  innerComponentCost: '',
  productType: selectTypeInitialValues,
  productSubType: selectTypeInitialValues,
  propagateToOrganizations: false,
};

export const excipientInitialValue: AddExcipientFormType = {
  name: '',
};

export const excipientLabelInitialValue: AddExcipientLabelFormType = {
  name: '',
};

export const excipientTypeInitialValue: AddExcipientTypeFormType = {
  name: '',
  loadAdd: '',
  loadAddUnit: LoadAddUnit.Mg,
  perKgCost: '',
  excipientLabel: '',
  productType: selectTypeInitialValues,
};

export const productExcipientInitialValue: ProductExcipientFormType = {
  perKgCost: '',
  percentage: '',
  excipient: selectTypeInitialValues,
  suppliers: [],
};

export const coatingFormulationComponentInitialValues: CoatingFormulationComponentFormType = {
  weight: '',
  sandComponent: selectTypeInitialValues,
  sandCoatingFormulation: selectTypeInitialValues,
};

export const bottleSizeInitialValue: AddBottleSizeFormType = {
  cc: '',
  oz: '',
  name: '',
  lidSize: '',
  productTypes: [],
};

export const servingSizeInitialValue: AddServingSizeFormType = {
  size: '',
};

export const bottlePriceInitialValue: AddBottlePriceFormType = {
  price: '',
  isCr: false,
  type: '',
  suppliers: [],
  bottleSize: selectTypeInitialValues,
};

export const bottleCapacityInitialValue: AddBottleCapacityFormType = {
  fillingPercentage: '',
  lohCost: '',
  productSize: selectTypeInitialValues,
  productType: selectTypeInitialValues,
  servingSize: selectTypeInitialValues,
  bottleSize: selectTypeInitialValues,
};

export const sweetenerSystemInitialValue: AddSweetenerSystemFormType = {
  name: '',
  mgPerServing: '',
  costPerServing: '',
  nature: '',
  size: '',
  type: '',
  productSize: selectTypeInitialValues,
  productType: selectTypeInitialValues,
  productFormulationType: selectTypeInitialValues,
};

export const sweetenerComponentInitialValue: AddSweetenerComponentFormType = {
  name: '',
  costPerKg: '',
};

export const productIngredientInitialValue: ProductIngredientFormType = {
  name: '',
};

export const sandCoatingInitialValue: SandCoatingFormType = {
  name: '',
};

export const sandCoatingFormulationInitialValue: SandCoatingFormulationFormType = {
  weight: '',
  cost: '',
  productSize: selectTypeInitialValues,
  sandCoating: selectTypeInitialValues,
  productType: selectTypeInitialValues,
  subProductType: selectTypeInitialValues,
};

export const sandComponentInitialValue: SandComponentFormType = {
  name: '',
};

export const sweetenerElementInitialValue: AddSweetenerElementFormType = {
  qtPerMg: '',
  sweetenerComponent: selectTypeInitialValues,
  sweetenerSystem: selectTypeInitialValues,
};

export const formulationSyrupTypeInitialValue: FormulationSyrupTypeFormType = {
  name: '',
  cost: '',
  weight: '',
  productType: selectTypeInitialValues,
  productSize: selectTypeInitialValues,
  subProductType: selectTypeInitialValues,
  productFormulationType: selectTypeInitialValues,
};

export const ingredientSyrupTypeInitialValue: IngredientSyrupTypeFormType = {
  percentage: '',
  productIngredient: selectTypeInitialValues,
};

export const capsuleTypeInitialValue: CapsuleTypeFormType = {
  name: '',
  description: '',
};

export const capsulePriceInitialValue: CapsulePriceFormType = {
  price: '',
  capacity: '',
  density: selectTypeInitialValues,
  supplier: selectTypeInitialValues,
  capsuleType: selectTypeInitialValues,
  productSize: selectTypeInitialValues,
};

export const baseCoatingInitialValue: BaseCoatingFormType = {
  name: '',
  loadAdd: '',
  costPerKg: '',
  productType: selectTypeInitialValues,
};

export const dvConstraintsInitialValue: DvConstraintsFormType = {
  name: '',
  foodComponent: '',
  infants: '',
  adultsAndChildren: '',
  children1To3Years: '',
  pregnantAndLactatingWomen: '',
  unit: '',
};
